import { createContext, useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Sentry from '@sentry/react';

import { User, UserRole } from '../types/user';
import { TOKEN_KEY, USER_KEY } from '../constants/keys';
import { Authenticated } from '../types/auth';
import { canAccessAdminDashboard, canManageUsers } from '../utils/roles';
import {
  getLocaleFromPath,
  getStoredLocale,
  localizePath,
  resolveOfflineLocale,
  setStoredLocale,
} from '../i18n/locale-path';
import { defaultLocale } from '../i18n';

type AuthContextType = {
  user: User|null;
  token: string|null;
  setAuth: (auth: Authenticated) => void;
  removeAuth: (path?: string) => void;
  isAdmin: () => boolean;
  canManageUsers: () => boolean;
  canAccessAdminDashboard: () => boolean;
  updateUser: (user: User) => void;
}

type AuthProviderProps = {
  children: React.ReactNode;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export const AuthContextProvider = ({ children }: AuthProviderProps) => {
  const navigate = useNavigate();

  const [user, setUser] = useState<User|null>(() => {
    const storedUser = localStorage.getItem(USER_KEY);
    return storedUser ? JSON.parse(storedUser) : null;
  })

  const [token, setToken] = useState<string | null>(() => {
    const storedToken = localStorage.getItem(TOKEN_KEY);
    return storedToken ? JSON.parse(storedToken) : null;
  })

  const syncSentryUser = useCallback((currentUser: User | null) => {
    if (currentUser) {
      Sentry.setUser({
        email: currentUser.email,
        username: `${currentUser.firstName} ${currentUser.lastName}`.trim(),
      });
    } else {
      Sentry.setUser(null);
    }
  }, []);

  const removeAuth = useCallback((path?: string) => {
    const preservedLocale =
      getLocaleFromPath(window.location.pathname) ??
      getStoredLocale() ??
      defaultLocale;
    localStorage.clear();
    setStoredLocale(preservedLocale);
    setUser(null);
    setToken(null);
    syncSentryUser(null);
    const locale = preservedLocale || resolveOfflineLocale();
    navigate(
      localizePath(locale, '/auth/login'),
      path ? { state: { path } } : { replace: true }
    );
  }, [navigate, syncSentryUser])

  const setAuth = useCallback(({token, user}: Authenticated) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    localStorage.setItem(TOKEN_KEY, JSON.stringify(token));
    setUser(user);
    setToken(token);
    syncSentryUser(user);
  }, [syncSentryUser])

  const isAdmin = useCallback(() => user?.role === UserRole.admin, [user?.role])
  const canManageUsersFn = useCallback(() => canManageUsers(user?.role), [user?.role])
  const canAccessAdminDashboardFn = useCallback(() => canAccessAdminDashboard(user?.role), [user?.role])

  const updateUser = useCallback((user: User) => {
    localStorage.setItem(USER_KEY, JSON.stringify(user));
    setUser(user);
    syncSentryUser(user);
  }, [syncSentryUser])

  useEffect(() => {
    syncSentryUser(user);
  }, [user, syncSentryUser]);

  return (
    <AuthContext.Provider value={{user, token, setAuth, removeAuth, isAdmin, canManageUsers: canManageUsersFn, canAccessAdminDashboard: canAccessAdminDashboardFn, updateUser}}>
      {children}
    </AuthContext.Provider>
  )
}